import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';

// ── Google Apps Script Web App URL ──────────────────────────
// After deploying the Apps Script, paste your URL below:
const SHEET_ENDPOINT = 'https://script.google.com/macros/s/AKfycbzkPwZSQaeldDclIrZFE8ojPhzARQ4ELEwaeeS5ZDl2G0yDWbu_Q1fQ4XJkHP_WHyhR/exec';
// ────────────────────────────────────────────────────────────


export default function AuditForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Read URL params set by CroPredictions section
    const emailFromUrl = searchParams.get('email') || '';
    const fromPredictions = searchParams.get('from') === 'predictions';

    const [name, setName] = useState('');
    const [email, setEmail] = useState(emailFromUrl);
    const [phone, setPhone] = useState('');
    const [company, setCompany] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});
    const [isSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [extraFieldsRevealed, setExtraFieldsRevealed] = useState(false);

    useEffect(() => {
        if (name.trim() !== '' && email.trim() !== '') {
            setExtraFieldsRevealed(true);
        }
    }, [name, email]);

    const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    const isValidPhone = (val) => /^[+]?[\d\s\-()]{7,15}$/.test(val);

    const validate = () => {
        const newErrors = {};
        if (!name.trim()) {
            newErrors.name = 'Full name is required';
        }
        if (!email.trim()) {
            newErrors.email = 'Email address is required';
        } else if (!isValidEmail(email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!isValidPhone(phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }
        if (!company.trim()) {
            newErrors.company = 'Company name is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // If extra fields not yet shown, reveal them and validate first two fields
        if (!extraFieldsRevealed) {
            const earlyErrors = {};
            if (!name.trim()) earlyErrors.name = 'Full name is required';
            if (!email.trim()) earlyErrors.email = 'Email address is required';
            else if (!isValidEmail(email)) earlyErrors.email = 'Please enter a valid email address';
            setErrors(earlyErrors);
            if (Object.keys(earlyErrors).length === 0) setExtraFieldsRevealed(true);
            return;
        }

        if (!validate()) return;

        setIsLoading(true);

        const payload = { name, email, phone, company, notes, free3Prediction: fromPredictions };

        // Fire both endpoints simultaneously — form succeeds if at least one works
        Promise.allSettled([
            // 1️⃣  PHP backend (email notification)
            fetch('https://thespinnennetz.com/sn-forms/book-audit.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }),
            // 2️⃣  Google Sheets (via Apps Script Web App)
            fetch(SHEET_ENDPOINT, {
                method: 'POST',
                // NOTE: 'no-cors' is required for Apps Script Web Apps called from a browser.
                // We must use 'text/plain' because 'application/json' triggers a blocked CORS preflight.
                mode: 'no-cors',
                headers: { 'Content-Type': 'text/plain;charset=utf-8' },
                body: JSON.stringify(payload),
            }),
        ])
            .then((results) => {
                results.forEach((result, i) => {
                    if (result.status === 'rejected') {
                        console.warn(`Endpoint ${i + 1} failed:`, result.reason);
                    }
                });
                console.log('Audit form submitted successfully', results);
                navigate('/book-audit-thankyou', { state: { successData: { name, company, email, phone } } });
            })
            .catch((err) => {
                console.error('Unexpected submission error:', err);
                navigate('/book-audit-thankyou', { state: { successData: { name, company, email, phone } } }); // still show success — don't block the user
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const isThankYouRoute = location.pathname === '/book-audit-thankyou';

    if (isThankYouRoute || isSubmitted) {
        const submittedData = location.state?.successData || { name, company, email, phone };
        const dName = submittedData.name || 'there';
        const dCompany = submittedData.company ? <span> from <strong>{submittedData.company}</strong></span> : '';
        const dEmail = submittedData.email || 'your email';
        const dPhone = submittedData.phone ? <span> or <strong>{submittedData.phone}</strong></span> : '';

        return (
            <div className="audit-success-state">
                <div className="success-icon-badge">✓</div>
                <h3 className="success-title">Audit Requested!</h3>
                <p className="success-desc">
                    Thank you, <strong>{dName}</strong>{dCompany}. We've received your request. Our conversion experts will analyze your site and contact you at <strong>{dEmail}</strong>{dPhone} within 24 hours.
                </p>
                <div style={{ marginTop: '24px' }}>
                    <Link to="/" className="sn-submit-btn" style={{ textDecoration: 'none' }}>
                        Back to Home Page
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <h2 className="audit-card-title">Book Free CRO Audit</h2>
            <p className="audit-card-desc">
                Request a comprehensive, data-backed conversion audit. We will identify friction points on your website and provide actionable solutions to boost your sales.
            </p>

            <form onSubmit={handleSubmit} className="audit-form" noValidate>
                {/* Row 1: Full Name & Email */}
                <Row>
                    <Col md={6}>
                        <div className="sn-floating-group">
                            <input
                                id="name-input"
                                type="text"
                                className={`sn-form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder=" "
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (errors.name) setErrors(prev => ({ ...prev, name: null }));
                                }}
                            />
                            <label htmlFor="name-input" className={errors.name ? 'label-error' : ''}>Full Name</label>
                            {errors.name && <div className="sn-invalid-feedback">{errors.name}</div>}
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="sn-floating-group">
                            <input
                                id="email-input"
                                type="email"
                                className={`sn-form-control ${errors.email ? 'is-invalid' : ''}`}
                                placeholder=" "
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (errors.email) setErrors(prev => ({ ...prev, email: null }));
                                }}
                            />
                            <label htmlFor="email-input" className={errors.email ? 'label-error' : ''}>Email Address</label>
                            {errors.email && <div className="sn-invalid-feedback">{errors.email}</div>}
                        </div>
                    </Col>
                </Row>

                {/* Extra fields — revealed once name + email have values */}
                {extraFieldsRevealed && (
                    <div className="extra-fields-section">
                        <Row>
                            <Col md={6}>
                                <div className="sn-floating-group">
                                    <input
                                        id="phone-input"
                                        type="tel"
                                        className={`sn-form-control ${errors.phone ? 'is-invalid' : ''}`}
                                        placeholder=" "
                                        value={phone}
                                        onChange={(e) => {
                                            setPhone(e.target.value);
                                            if (errors.phone) setErrors(prev => ({ ...prev, phone: null }));
                                        }}
                                    />
                                    <label htmlFor="phone-input" className={errors.phone ? 'label-error' : ''}>Phone Number</label>
                                    {errors.phone && <div className="sn-invalid-feedback">{errors.phone}</div>}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="sn-floating-group">
                                    <input
                                        id="company-input"
                                        type="text"
                                        className={`sn-form-control ${errors.company ? 'is-invalid' : ''}`}
                                        placeholder=" "
                                        value={company}
                                        onChange={(e) => {
                                            setCompany(e.target.value);
                                            if (errors.company) setErrors(prev => ({ ...prev, company: null }));
                                        }}
                                    />
                                    <label htmlFor="company-input" className={errors.company ? 'label-error' : ''}>Company Name</label>
                                    {errors.company && <div className="sn-invalid-feedback">{errors.company}</div>}
                                </div>
                            </Col>
                        </Row>

                        <div className="sn-floating-group">
                            <textarea
                                id="notes-input"
                                rows="3"
                                className="sn-form-control sn-textarea"
                                placeholder=" "
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            ></textarea>
                            <label htmlFor="notes-input">How can we help? (Optional)</label>
                        </div>
                    </div>
                )}

                <button type="submit" className="sn-submit-btn" disabled={isLoading}>
                    {isLoading ? (
                        <>Submitting…</>
                    ) : (
                        <>
                            Request Free Audit{' '}
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
                                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </>
                    )}
                </button>
            </form>
            <p className="audit-card-fine-print">
                No spam. No agency pitch.<br/>Just honest findings delivered within 2 business days.
            </p>
        </>
    );
}
